import { useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import { useEscapeLayer } from '../escStack';
import { useFocusTrap } from '../focusTrap';
import { IconButton } from './Button';
import s from './Modal.module.css';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  /** dialog = the centred 620px card; sheet = the 720px top-aligned new-preset modal. */
  variant?: 'dialog' | 'sheet';
  /** id of the element naming the dialog; set automatically by <ModalHeader>/<ConfirmHeader> via context-free ids. */
  labelledBy?: string;
  label?: string;
  children: ReactNode;
  className?: string;
};

/**
 * Modal: scrim + blur, fadeup, scrim click closes, Esc closes (innermost layer only),
 * focus moves in, Tab is trapped, focus returns to the opener on close.
 */
export function Modal({ open, onClose, variant = 'dialog', labelledBy, label, children, className }: ModalProps) {
  const panel = useRef<HTMLDivElement>(null);
  useEscapeLayer(open, onClose);
  useFocusTrap(panel, open);
  if (!open) return null;
  return createPortal(
    <div className={cx(s.scrim, variant === 'sheet' && s.sheetScrim)} onClick={onClose} data-testid="modal-scrim">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        tabIndex={-1}
        className={cx(s.panel, variant === 'sheet' && s.sheet, className)}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Form-modal header: 26px title, 13px sub-line, a 36px close button. */
export function ModalHeader({
  id,
  title,
  sub,
  onClose,
  closeLabel,
}: {
  id: string;
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  closeLabel: string;
}) {
  return (
    <div className={s.header}>
      <div className={s.titles}>
        <h2 id={id} className={s.title}>
          {title}
        </h2>
        {sub && <span className={s.sub}>{sub}</span>}
      </div>
      <IconButton icon="x" label={closeLabel} tone="outline" size={36} onClick={onClose} />
    </div>
  );
}

/** Confirm-modal header: 24px title and the explanatory text. */
export function ConfirmHeader({ id, title, text }: { id: string; title: ReactNode; text?: ReactNode }) {
  return (
    <div className={s.confirmHead}>
      <h2 id={id} className={s.confirmTitle}>
        {title}
      </h2>
      {text && <span className={s.confirmText}>{text}</span>}
    </div>
  );
}

/** The key-value block on #0D0D0F used by confirm modals. */
export function KeyValueList({ rows }: { rows: { key: string; label: ReactNode; value: ReactNode }[] }) {
  return (
    <dl className={s.kv}>
      {rows.map((r) => (
        <div key={r.key} className={s.kvRow}>
          <dt className={s.kvKey}>{r.label}</dt>
          <dd className={s.kvValue}>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ModalFooter({ children, padTop }: { children: ReactNode; padTop?: boolean }) {
  return <div className={cx(s.footer, padTop && s.footerPad)}>{children}</div>;
}

/** A stable id for a modal title. */
export function useModalTitleId(): string {
  return useId();
}
